import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Share2, RotateCw, Users, ArrowLeft, Copy, Sparkles, Flame } from "lucide-react";
import { toast } from "sonner";
import { loadLastResult, clearDaily } from "../lib/storage";
import { ShareResultCard } from "../components/ShareResultCard";
import { MashFactorBanner } from "../components/MashFactorBanner";
import { DAILY_MATCHUPS, DIFFICULTIES } from "../data/gameData";
import { findCeleb } from "../data/celebrities";
import { todaysMashFactor } from "../data/mashFactors";
import { getBestStreak, getLastStreak } from "../lib/streak";

// Animated number counter
const Counter = ({ to, duration = 1.2, delay = 0, suffix = "%" }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const t0 = performance.now() + delay * 1000;
    const step = (t) => {
      if (t < t0) { raf = requestAnimationFrame(step); return; }
      const p = Math.min(1, (t - t0) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, duration, delay]);
  return <span className="tabular">{n}{suffix}</span>;
};

const Bar = ({ label, value, accent, delay }) => (
  <div>
    <div className="flex justify-between text-xs text-[#FDFBF7]/70 mb-1.5">
      <span className="tracking-[0.2em] uppercase font-semibold">{label}</span>
      <span className="font-display text-xl" style={{ color: accent }}>
        <Counter to={value} delay={delay} />
      </span>
    </div>
    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${value}%` }}
        transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
        className="h-full rounded-full"
        style={{ background: accent, boxShadow: `0 0 12px ${accent}` }}
      />
    </div>
  </div>
);

export default function Results() {
  const nav = useNavigate();
  const [result, setResult] = useState(null);
  const [phase, setPhase] = useState("intro"); // intro -> archetype -> stats
  const [confetti, setConfetti] = useState([]);

  useEffect(() => {
    const r = loadLastResult();
    if (!r) return;
    setResult(r);
    const dots = Array.from({ length: 36 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.8,
      color: ["#FF107A", "#00E5FF", "#A855F7", "#FFD700", "#FDFBF7"][i % 5],
      size: 6 + Math.random() * 6,
    }));
    setConfetti(dots);

    const t1 = setTimeout(() => setPhase("archetype"), 1400);
    const t2 = setTimeout(() => setPhase("stats"), 3200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  if (!result) {
    return (
      <div className="min-h-screen spotlight grain">
        <div className="max-w-md mx-auto px-5 pt-24 pb-nav text-center">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-[#FF107A]/15 border border-[#FF107A]/40 items-center justify-center mb-6">
            <Sparkles size={28} className="text-[#FF107A]" />
          </div>
          <h1 className="font-display text-5xl tracking-tight leading-none">No result yet</h1>
          <p className="mt-3 text-sm text-[#FDFBF7]/70 max-w-xs mx-auto">
            Play five rounds of today&rsquo;s Smash to unlock your archetype and shareable card.
          </p>
          <button
            data-testid="results-play-btn"
            onClick={() => nav("/daily")}
            className="mt-8 rounded-full bg-[#FF107A] px-8 py-4 font-bold glow-pink"
          >
            Play the Daily Smash
          </button>
        </div>
      </div>
    );
  }

  const hardestRound = DAILY_MATCHUPS.find((r) => r.id === result.hardestRoundId);
  const hardestLabel = hardestRound
    ? `${findCeleb(hardestRound.aId).name} vs ${findCeleb(hardestRound.bId).name}`
    : null;
  const best = getBestStreak();
  const last = getLastStreak();

  const share = async () => {
    const siteUrl = typeof window !== "undefined" ? window.location.origin : "https://celebritysmash.app";
    const text = `Celebrity Smash — I'm a ${result.archetype.title}. Prediction ${result.predictionAccuracy}% · Contrarian ${result.contrarianScore}%.\nPlay: ${siteUrl}`;
    try {
      if (navigator.share) await navigator.share({ title: "Celebrity Smash", text, url: siteUrl });
      else {
        await navigator.clipboard.writeText(text);
        toast.success("Result copied — ready to paste or screenshot.");
      }
    } catch { toast.message("Share card ready to screenshot."); }
  };
  const copyText = async () => {
    const siteUrl = typeof window !== "undefined" ? window.location.origin : "https://celebritysmash.app";
    const text = `Celebrity Smash — ${result.archetype.title}\nPrediction ${result.predictionAccuracy}% · Contrarian ${result.contrarianScore}% · Creative ${result.creativePreference}% · Values ${result.valuesPreference}%\nPlay: ${siteUrl}`;
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard.");
  };
  const playAgain = () => { clearDaily(); nav("/daily"); };

  return (
    <div className="min-h-screen spotlight grain scanlines relative overflow-hidden">
      {/* Confetti */}
      <div className="pointer-events-none absolute inset-0 z-0">
        {confetti.map((d) => (
          <motion.div
            key={d.id}
            initial={{ y: -30, opacity: 0, rotate: 0 }}
            animate={{ y: "115vh", opacity: 1, rotate: 720 }}
            transition={{ duration: 3.8, delay: d.delay, ease: "easeIn" }}
            style={{ left: `${d.left}%`, background: d.color, width: d.size, height: d.size, borderRadius: 2 }}
            className="absolute top-0"
          />
        ))}
      </div>

      <div className="relative z-10 max-w-md mx-auto px-5 pt-6 pb-nav">
        <button data-testid="results-back-btn" onClick={() => nav("/home")} className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
          <ArrowLeft size={16} /> Home
        </button>

        {/* Phase 1: Intro slam */}
        <AnimatePresence mode="wait">
          {phase === "intro" && (
            <motion.div
              key="intro"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -20 }}
              className="min-h-[70vh] flex flex-col items-center justify-center text-center"
            >
              <div className="text-[11px] tracking-[0.4em] uppercase text-[#00E5FF] font-bold">Revealing</div>
              <h1 className="font-display text-7xl sm:text-8xl leading-[0.85] tracking-tight mt-3 slam-in">
                YOUR<br /><span className="text-[#FF107A]">RESULT</span>
              </h1>
              <div className="mt-6 flex gap-2">
                <span className="w-2 h-2 rounded-full bg-[#FF107A] animate-pulse" />
                <span className="w-2 h-2 rounded-full bg-[#00E5FF] animate-pulse" style={{ animationDelay: "0.2s" }} />
                <span className="w-2 h-2 rounded-full bg-[#A855F7] animate-pulse" style={{ animationDelay: "0.4s" }} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {phase !== "intro" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6"
          >
            <div className="text-[11px] tracking-[0.4em] uppercase text-[#00E5FF] font-bold">You are</div>
            <motion.h1
              initial={{ scale: 1.4, opacity: 0, filter: "blur(8px)" }}
              animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
              transition={{ type: "spring", stiffness: 200, damping: 22 }}
              className="font-display text-[3.8rem] sm:text-7xl leading-[0.85] tracking-tight mt-2"
            >
              <span className="text-[#FF107A]">{result.archetype.title}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-3 font-display text-lg tracking-[0.15em] uppercase text-[#FDFBF7]/85"
            >
              {result.archetype.tagline}
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-3 text-[#FDFBF7]/70 text-sm leading-relaxed"
            >
              {result.archetype.description}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="mt-4"
            >
              <MashFactorBanner label={todaysMashFactor().label} size="sm" testId="results-mash-banner" />
            </motion.div>
          </motion.div>
        )}

        {phase === "stats" && (
          <>
            <div className="mt-8 space-y-4">
              <Bar label="Prediction Accuracy" value={result.predictionAccuracy} accent="#FF107A" delay={0.0} />
              <Bar label="Majority Alignment" value={result.majorityAlignment} accent="#00E5FF" delay={0.1} />
              <Bar label="Contrarian Score" value={result.contrarianScore} accent="#A855F7" delay={0.2} />
              <Bar label="Creative Preference" value={result.creativePreference} accent="#FFD700" delay={0.3} />
              <Bar label="Values Preference" value={result.valuesPreference} accent="#F472B6" delay={0.4} />
            </div>

            {/* Traits */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-6 rounded-2xl border border-white/10 bg-[#16141D]/80 p-4"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold mb-2">Strongest signals</div>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full bg-[#FF107A]/15 border border-[#FF107A]/40 text-sm">
                  Top trait · {result.topReasonLabel}
                </span>
                <span className="px-3 py-1 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-sm">
                  Deciding factor · {result.topFactor}
                </span>
              </div>
              <p className="mt-3 text-xs text-[#FDFBF7]/60 italic">Surprise: {result.archetype.surprise}</p>
            </motion.div>

            {/* Difficulty profile */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-4 rounded-2xl border border-white/10 bg-[#16141D]/80 p-4"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A855F7] font-bold mb-2">Difficulty profile</div>
              <div className="grid grid-cols-4 gap-2">
                {DIFFICULTIES.map((d) => (
                  <div key={d.id} className="text-center">
                    <div className="font-display text-3xl text-[#FDFBF7]">{result.difficultyCounts[d.id] || 0}</div>
                    <div className="text-[9px] tracking-[0.15em] uppercase text-[#FDFBF7]/50 mt-0.5 leading-tight">{d.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Streak / high score */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              data-testid="results-streak-card"
              className="mt-4 rounded-2xl border border-[#FFD700]/30 bg-[#FFD700]/5 p-4 flex items-center gap-3"
            >
              <div className="w-11 h-11 rounded-xl bg-[#FFD700]/15 border border-[#FFD700]/40 flex items-center justify-center text-[#FFD700]">
                <Flame size={20} />
              </div>
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#FFD700] font-bold">Solo high score</div>
                <div className="text-sm text-[#FDFBF7]/85 mt-0.5">
                  Best streak: <span data-testid="results-best-streak" className="font-display text-xl text-[#FDFBF7] tabular ml-1">{best}</span>
                  <span className="text-[#FDFBF7]/50"> · This game: <span data-testid="results-last-streak" className="font-display text-xl text-[#FDFBF7] tabular">{last}</span></span>
                </div>
              </div>
            </motion.div>

            {/* Share card */}
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.8, type: "spring", stiffness: 160 }}
              className="mt-6"
            >
              <ShareResultCard result={result} hardestMatchup={hardestLabel} />
            </motion.div>

            {/* Actions */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button data-testid="share-result-btn" onClick={share} className="rounded-full bg-[#FF107A] py-3 font-bold flex items-center justify-center gap-2 glow-pink">
                <Share2 size={16} /> Share
              </button>
              <button data-testid="copy-result-btn" onClick={copyText} className="rounded-full border-2 border-[#00E5FF] text-[#00E5FF] py-3 font-bold flex items-center justify-center gap-2 hover:bg-[#00E5FF]/10">
                <Copy size={16} /> Copy
              </button>
              <button data-testid="challenge-friend-btn" onClick={() => nav("/create")} className="rounded-full border-2 border-[#A855F7] text-[#FDFBF7] py-3 font-bold flex items-center justify-center gap-2 hover:bg-[#A855F7]/10">
                <Users size={16} /> Challenge Friend
              </button>
              <button data-testid="play-again-btn" onClick={playAgain} className="rounded-full border-2 border-white/15 py-3 font-bold flex items-center justify-center gap-2 hover:border-white/30">
                <RotateCw size={16} /> Play Again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
